/* LCT — Outils du tiroir panier : panneaux (note, livraison, code promo)
   (la note de commande utilise le composant <cart-note> natif de cart.js) */

(() => {
    const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

    /* ------------------------------------------------------------------ */
    /* Barre d'outils + panneaux qui montent depuis le bas                 */
    /* ------------------------------------------------------------------ */
    if (!customElements.get('lct-cart-tools')) {
        class LctCartTools extends HTMLElement {
            connectedCallback() {
                this.drawerInner = this.closest('.drawer__inner');
                this.current = null;

                this.addEventListener('click', (event) => {
                    const opener = event.target.closest('[data-sheet-open]');
                    if (opener) {
                        event.preventDefault();
                        this.open(opener.dataset.sheetOpen, opener);
                        return;
                    }
                    const noteSave = event.target.closest('[data-note-save]');
                    if (noteSave) {
                        event.preventDefault();
                        this.saveNote(noteSave);
                        return;
                    }
                    if (event.target.closest('[data-sheet-close]')) {
                        event.preventDefault();
                        this.close();
                    }
                });

                // Échap ferme le panneau sans fermer tout le tiroir
                this.addEventListener('keyup', (event) => {
                    if (event.code === 'Escape' && this.current) {
                        event.stopPropagation();
                        this.close();
                    }
                });

                // Badge "note" mis à jour pendant la saisie
                const note = this.querySelector('textarea[name="note"]');
                const noteTab = this.querySelector('[data-sheet-open="note"]');
                if (note && noteTab) {
                    note.addEventListener('input', () => noteTab.classList.toggle('has-value', note.value.trim() !== ''));
                }

                // Réouverture après re-rendu du tiroir (ex. code promo appliqué)
                if (window.__lctOpenSheet) {
                    const name = window.__lctOpenSheet;
                    window.__lctOpenSheet = null;
                    this.open(name, null, { instant: true });
                }
            }

            disconnectedCallback() {
                this.drawerInner?.classList.remove('lct-sheet-open');
            }

            async saveNote(button) {
                const note = this.querySelector('textarea[name="note"]');
                if (!note) return;
                button.disabled = true;
                button.closest('cart-note')?.classList.add('is-loading');
                try {
                    await fetch(`${routes.cart_update_url}.js`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                        body: JSON.stringify({ note: note.value }),
                    });
                    this.querySelector('[data-sheet-open="note"]')?.classList.toggle('has-value', note.value.trim() !== '');
                    this.close();
                } catch (e) {
                    console.error(e);
                } finally {
                    button.disabled = false;
                    button.closest('cart-note')?.classList.remove('is-loading');
                }
            }

            getSheet(name) {
                return this.querySelector(`[data-sheet="${name}"]`);
            }

            open(name, trigger = null, { instant = false } = {}) {
                const sheet = this.getSheet(name);
                if (!sheet) return;
                if (this.current && this.current !== sheet) this.close({ restoreFocus: false });

                this.current = sheet;
                this.trigger = trigger || this.querySelector(`[data-sheet-open="${name}"]`);

                if (instant) this.classList.add('lct-ct--instant');
                this.classList.add('is-open');
                this.drawerInner?.classList.add('lct-sheet-open');
                sheet.classList.add('is-open');
                sheet.setAttribute('aria-hidden', 'false');
                this.trigger?.setAttribute('aria-expanded', 'true');

                if (instant) {
                    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('lct-ct--instant')));
                } else {
                    const field = sheet.querySelector('input:not([type="hidden"]), textarea');
                    setTimeout(() => field?.focus({ preventScroll: true }), 320);
                }
            }

            close({ restoreFocus = true } = {}) {
                const sheet = this.current;
                if (!sheet) return;
                sheet.classList.remove('is-open');
                sheet.setAttribute('aria-hidden', 'true');
                this.classList.remove('is-open');
                this.drawerInner?.classList.remove('lct-sheet-open');
                this.trigger?.setAttribute('aria-expanded', 'false');
                if (restoreFocus) this.trigger?.focus({ preventScroll: true });
                this.current = null;
            }
        }

        customElements.define('lct-cart-tools', LctCartTools);
    }

    /* ------------------------------------------------------------------ */
    /* Code promo : fonctions communes                                     */
    /* ------------------------------------------------------------------ */
    const lctDiscount = {
        // Section de la page panier (/cart), si on y est
        pageSection() {
            return document.querySelector('[data-lct-cart-section]')?.dataset.lctCartSection || null;
        },

        async update(codes) {
            const sections = ['cart-drawer', 'cart-icon-bubble'];
            const page = this.pageSection();
            if (page) sections.push(page);

            const response = await fetch(`${routes.cart_update_url}.js`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify({
                    discount: codes.join(','),
                    sections,
                    sections_url: window.location.pathname,
                }),
            });
            if (!response.ok) throw new Error(response.status);
            return response.json();
        },

        replaceParts(html, selectors) {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            selectors.forEach((selector) => {
                const target = document.querySelector(selector);
                const source = doc.querySelector(selector);
                if (target && source) target.innerHTML = source.innerHTML;
            });
        },

        // Re-rendu après changement de code.
        // openSheet = panneau (tiroir) ou accordéon (page) à rouvrir ; null = fermé
        render(state, { message = null, openSheet = null } = {}) {
            const sections = state.sections || {};
            const page = this.pageSection();

            // --- Page panier : on remplace les blocs de la page, et le tiroir en silence
            if (page && sections[page]) {
                window.__lctDiscountMessage = message;
                this.replaceParts(sections[page], [
                    '#main-cart-items .js-contents',
                    '#main-cart-footer .js-contents',
                    '#LctCartPage-Tools',
                    '#LctCart-SummaryEnd',
                ]);
                window.__lctDiscountMessage = null;
                if (openSheet) {
                    const details = document.getElementById('LctCartPage-Discount');
                    if (details) details.open = true;
                }
                if (sections['cart-drawer']) {
                    this.replaceParts(sections['cart-drawer'], ['#CartDrawer']);
                    const drawer = document.querySelector('cart-drawer');
                    document.getElementById('CartDrawer-Overlay')?.addEventListener('click', () => drawer?.close());
                }
                if (sections['cart-icon-bubble']) {
                    const bubble = document.getElementById('cart-icon-bubble');
                    const source = new DOMParser()
                        .parseFromString(sections['cart-icon-bubble'], 'text/html')
                        .querySelector('.shopify-section');
                    if (bubble && source) bubble.innerHTML = source.innerHTML;
                }
                return;
            }

            // --- Tiroir panier
            const drawer = document.querySelector('cart-drawer');
            if (drawer && sections['cart-drawer']) {
                window.__lctDiscountMessage = message;
                window.__lctOpenSheet = openSheet;
                drawer.renderContents({ id: null, sections });
            } else {
                window.location.reload();
            }
        },

        parseCodes(value) {
            return (value || '').split(',').map((c) => c.trim()).filter(Boolean);
        },
    };

    /* ------------------------------------------------------------------ */
    /* Code promo : panneau de saisie                                      */
    /* ------------------------------------------------------------------ */
    if (!customElements.get('lct-cart-discount')) {
        class LctCartDiscount extends HTMLElement {
            connectedCallback() {
                this.form = this.querySelector('form');
                this.input = this.querySelector('input[name="code"]');
                this.button = this.form.querySelector('button[type="submit"]');
                this.message = this.querySelector('.lct-ct__msg');

                this.form.addEventListener('submit', (event) => {
                    event.preventDefault();
                    this.apply(this.input.value);
                });

                // Message transmis après un re-rendu du tiroir (erreur)
                if (window.__lctDiscountMessage) {
                    const { text, type } = window.__lctDiscountMessage;
                    window.__lctDiscountMessage = null;
                    this.showMessage(text, type);
                }
            }

            get codes() {
                return lctDiscount.parseCodes(this.dataset.codes);
            }

            showMessage(text, type = '') {
                this.message.textContent = text || '';
                this.message.className = `lct-ct__msg${type ? ` lct-ct__msg--${type}` : ''}`;
            }

            setLoading(isLoading) {
                this.button.disabled = isLoading;
                this.input.disabled = isLoading;
                this.classList.toggle('is-loading', isLoading);
            }

            async apply(rawCode) {
                const code = (rawCode || '').trim();
                if (!code) {
                    this.showMessage('Saisissez un code promo.', 'error');
                    this.input.focus();
                    return;
                }
                const current = this.codes;
                if (current.some((c) => c.toLowerCase() === code.toLowerCase())) {
                    this.showMessage(`Le code « ${code} » est déjà appliqué.`, 'error');
                    return;
                }

                this.setLoading(true);
                this.showMessage('');
                try {
                    // On conserve les codes déjà présents (ex. ceux posés par l'app de bundles)
                    let state = await lctDiscount.update([...current, code]);
                    const entry = (state.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());

                    if (!entry || !entry.applicable) {
                        const remaining = (state.discount_codes || [])
                            .filter((d) => d.code.toLowerCase() !== code.toLowerCase())
                            .map((d) => d.code);
                        state = await lctDiscount.update(remaining);
                        // Code refusé : le panneau reste ouvert avec le message d'erreur
                        lctDiscount.render(state, {
                            openSheet: 'discount',
                            message: {
                                text: `Le code « ${code} » n'est pas valide ou ne s'applique pas à votre panier.`,
                                type: 'error',
                            },
                        });
                    } else {
                        // Code valide : le panneau se referme, la ligne de réduction apparaît avant le total
                        lctDiscount.render(state);
                    }
                } catch (e) {
                    console.error(e);
                    this.setLoading(false);
                    this.showMessage('Une erreur est survenue, veuillez réessayer.', 'error');
                }
            }
        }

        customElements.define('lct-cart-discount', LctCartDiscount);
    }

    /* ------------------------------------------------------------------ */
    /* Code promo : lignes de réduction avant le total (avec "Retirer")    */
    /* ------------------------------------------------------------------ */
    if (!customElements.get('lct-discount-lines')) {
        class LctDiscountLines extends HTMLElement {
            connectedCallback() {
                this.addEventListener('click', (event) => {
                    const button = event.target.closest('[data-remove-code]');
                    if (button) {
                        event.preventDefault();
                        this.remove(button.dataset.removeCode, button);
                    }
                });
            }

            async remove(code, button) {
                const row = button.closest('.lct-dl__row');
                row?.classList.add('is-loading');
                this.querySelectorAll('[data-remove-code]').forEach((b) => (b.disabled = true));
                try {
                    const codes = lctDiscount
                        .parseCodes(this.dataset.codes)
                        .filter((c) => c.toLowerCase() !== code.toLowerCase());
                    const state = await lctDiscount.update(codes);
                    lctDiscount.render(state);
                } catch (e) {
                    console.error(e);
                    row?.classList.remove('is-loading');
                    this.querySelectorAll('[data-remove-code]').forEach((b) => (b.disabled = false));
                }
            }
        }

        customElements.define('lct-discount-lines', LctDiscountLines);
    }

    /* ------------------------------------------------------------------ */
    /* Page panier : bouton "Enregistrer la note"                          */
    /* ------------------------------------------------------------------ */
    if (!customElements.get('lct-note-save')) {
        class LctNoteSave extends HTMLElement {
            connectedCallback() {
                this.button = this.querySelector('button');
                this.message = this.querySelector('.lct-ct__msg');
                this.button.addEventListener('click', () => this.save());
            }

            async save() {
                const note = this.closest('details, .lct-acc__body')?.querySelector('textarea[name="note"]');
                if (!note) return;
                this.button.disabled = true;
                this.classList.add('is-loading');
                try {
                    await fetch(`${routes.cart_update_url}.js`, {
                        method: 'POST',
                        headers: jsonHeaders,
                        body: JSON.stringify({ note: note.value }),
                    });
                    this.message.className = 'lct-ct__msg lct-ct__msg--success';
                    this.message.textContent = note.value.trim() ? 'Note enregistrée.' : 'Note supprimée.';
                } catch (e) {
                    console.error(e);
                    this.message.className = 'lct-ct__msg lct-ct__msg--error';
                    this.message.textContent = 'Une erreur est survenue, veuillez réessayer.';
                } finally {
                    this.button.disabled = false;
                    this.classList.remove('is-loading');
                }
            }
        }

        customElements.define('lct-note-save', LctNoteSave);
    }

    /* ------------------------------------------------------------------ */
    /* Estimation des frais de livraison                                   */
    /* ------------------------------------------------------------------ */
    if (!customElements.get('lct-shipping-estimator')) {
        const STORAGE_KEY = 'lct-shipping-estimate';

        class LctShippingEstimator extends HTMLElement {
            connectedCallback() {
                this.form = this.querySelector('form');
                this.country = this.querySelector('select[name="country"]');
                this.province = this.querySelector('select[name="province"]');
                this.zip = this.querySelector('input[name="zip"]');
                this.button = this.form.querySelector('button[type="submit"]');
                this.results = this.querySelector('.lct-ct__results');

                const saved = this.readSaved();
                this.selectCountry(saved.country || this.country.dataset.default);
                if (saved.province) this.province.value = saved.province;
                if (saved.zip) this.zip.value = saved.zip;

                this.country.addEventListener('change', () => this.updateProvinces());
                this.form.addEventListener('submit', (event) => {
                    event.preventDefault();
                    this.estimate();
                });
            }

            readSaved() {
                try {
                    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
                } catch (e) {
                    return {};
                }
            }

            save(data) {
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
                } catch (e) { }
            }

            selectCountry(value) {
                const option = Array.from(this.country.options).find((o) => o.value === value);
                if (option) this.country.value = value;
                this.updateProvinces();
            }

            updateProvinces() {
                const option = this.country.options[this.country.selectedIndex];
                let provinces = [];
                try {
                    provinces = JSON.parse(option?.dataset.provinces || '[]');
                } catch (e) { }

                this.province.innerHTML = '';
                if (provinces.length) {
                    provinces.forEach(([value, label]) => this.province.add(new Option(label, value)));
                    this.province.hidden = false;
                } else {
                    this.province.hidden = true;
                }
            }

            setLoading(isLoading) {
                this.button.disabled = isLoading;
                this.classList.toggle('is-loading', isLoading);
                this.button.setAttribute('aria-busy', isLoading);
            }

            formatPrice(amount) {
                const value = parseFloat(amount);
                if (!value) return 'Offerte';
                try {
                    return new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: this.dataset.currency || 'EUR',
                    }).format(value);
                } catch (e) {
                    return `${value.toFixed(2).replace('.', ',')} €`;
                }
            }

            errorText(json) {
                if (!json || typeof json !== 'object') return 'Impossible de calculer la livraison pour cette adresse.';
                const labels = { zip: 'Code postal', country: 'Pays', province: 'Région' };
                return Object.entries(json)
                    .map(([key, value]) => `${labels[key] ? `${labels[key]} : ` : ''}${[].concat(value).join(', ')}`)
                    .join(' ');
            }

            showError(text) {
                this.results.innerHTML = '';
                const p = document.createElement('p');
                p.className = 'lct-ct__msg lct-ct__msg--error';
                p.textContent = text;
                this.results.appendChild(p);
            }

            showRates(rates) {
                this.results.innerHTML = '';
                if (!rates.length) {
                    this.showError('Aucune option de livraison disponible pour cette adresse.');
                    return;
                }
                const list = document.createElement('ul');
                list.className = 'lct-ct__rates list-unstyled';
                rates
                    .slice()
                    .sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
                    .forEach((rate) => {
                        const li = document.createElement('li');
                        li.className = 'lct-ct__rate';
                        const name = document.createElement('span');
                        name.textContent = rate.name;
                        const price = document.createElement('strong');
                        price.textContent = this.formatPrice(rate.price);
                        if (!parseFloat(rate.price)) price.className = 'is-free';
                        li.append(name, price);
                        list.appendChild(li);
                    });
                this.results.appendChild(list);
            }

            async estimate() {
                const zip = this.zip.value.trim();
                if (!zip) {
                    this.showError('Saisissez votre code postal.');
                    this.zip.focus();
                    return;
                }

                const address = {
                    country: this.country.value,
                    province: this.province.hidden ? '' : this.province.value,
                    zip,
                };
                this.save(address);

                const params = new URLSearchParams();
                Object.entries(address).forEach(([key, value]) => params.append(`shipping_address[${key}]`, value));
                const query = params.toString();

                this.setLoading(true);
                this.results.innerHTML = '';

                try {
                    const prepare = await fetch(`${routes.cart_url}/prepare_shipping_rates.json?${query}`, {
                        method: 'POST',
                        headers: { Accept: 'application/json' },
                    });
                    if (!prepare.ok && prepare.status !== 202) {
                        this.showError(this.errorText(await prepare.json().catch(() => null)));
                        return;
                    }

                    for (let attempt = 0; attempt < 12; attempt++) {
                        const response = await fetch(`${routes.cart_url}/async_shipping_rates.json?${query}`, {
                            headers: { Accept: 'application/json' },
                        });
                        if (response.status === 200) {
                            const json = await response.json().catch(() => null);
                            if (json && json.shipping_rates) {
                                this.showRates(json.shipping_rates);
                                return;
                            }
                        } else if (response.status !== 202) {
                            this.showError(this.errorText(await response.json().catch(() => null)));
                            return;
                        }
                        await new Promise((resolve) => setTimeout(resolve, 500));
                    }
                    this.showError('Le calcul prend plus de temps que prévu, veuillez réessayer.');
                } catch (e) {
                    console.error(e);
                    this.showError('Une erreur est survenue, veuillez réessayer.');
                } finally {
                    this.setLoading(false);
                }
            }
        }

        customElements.define('lct-shipping-estimator', LctShippingEstimator);
    }
})();

/* ------------------------------------------------------------------
   Jauge « livraison offerte » animée (tiroir + page panier)
   Le thème remplace le HTML du panier à chaque changement de quantité :
   la nouvelle jauge repart de la largeur précédente puis glisse vers la nouvelle.
------------------------------------------------------------------ */
(() => {
    if (window.__lctShipBarAnim) return;
    window.__lctShipBarAnim = true;

    const SELECTOR = '.lct-drawer-ship__progress span, .lct-cart__progress span';
    let lastWidth = null;

    const animate = (bar) => {
        if (bar.dataset.lctAnimated) return;
        bar.dataset.lctAnimated = 'true';

        const target = parseFloat(bar.style.width);
        if (Number.isNaN(target)) return;

        const from = lastWidth === null ? 0 : lastWidth;
        lastWidth = target;
        if (from === target) return;

        const transition = bar.style.transition;
        bar.style.transition = 'none';
        bar.style.width = `${from}%`;
        bar.getBoundingClientRect(); // force le navigateur à appliquer la largeur de départ
        bar.style.transition = transition;
        requestAnimationFrame(() => {
            bar.style.width = `${target}%`;
        });
    };

    const scan = () => document.querySelectorAll(SELECTOR).forEach(animate);

    new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scan);
    } else {
        scan();
    }
})();

/* ------------------------------------------------------------------
   « Sas » pré-commande avant le paiement (tiroir + page panier)
   Si le panier contient des articles en pré-commande (étiquette
   .lct-backorder-pill dans le tiroir, encart .lct-cart__notice sur la page), le bouton
   « Commander » ouvre d'abord une fenêtre de confirmation.
   L'accord est enregistré dans les attributs du panier : il apparaît
   sur la commande dans l'admin (« Pré-commande acceptée »).
------------------------------------------------------------------ */
(() => {
    if (window.__lctBackorderGate) return;
    window.__lctBackorderGate = true;

    const NOTICE = '.lct-backorder-pill, .lct-drawer-notice, .lct-cart__notice';
    const CHECKOUT = 'button[name="checkout"], input[name="checkout"], #CartDrawer-Checkout';
    let dialog = null;
    let lastTrigger = null;

    const hasBackorders = () => Boolean(document.querySelector(NOTICE));

    const build = () => {
        dialog = document.createElement('div');
        dialog.className = 'lct-gate';
        dialog.hidden = true;
        dialog.innerHTML = `
            <div class="lct-gate__overlay" data-lct-gate-close></div>
            <div class="lct-gate__box" role="dialog" aria-modal="true" aria-labelledby="LctGateTitle" tabindex="-1">
                <div class="lct-gate__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="26" height="26" fill="none"><circle cx="12" cy="12" r="9.5" stroke="currentColor" stroke-width="1.6"/><path d="M12 6.8V12l3.4 2.1" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </div>
                <h2 class="lct-gate__title" id="LctGateTitle">Votre panier contient des articles en pré-commande</h2>
                <p class="lct-gate__text">
                    Certains articles de votre panier sont en pré-commande : ils sont commandés par nos soins et
                    généralement livrés sous 10 à 15 jours (sauf indication spécifique).
                    <strong>L'ensemble de votre commande sera envoyé en même temps.</strong>
                </p>
                <label class="lct-gate__check">
                    <input type="checkbox" data-lct-gate-accept>
                    <span>J'ai bien compris que ma commande sera expédiée en une seule fois, lorsque tous les articles seront disponibles.</span>
                </label>
                <div class="lct-gate__actions">
                    <button type="button" class="lct-gate__back" data-lct-gate-close>Revenir au panier</button>
                    <button type="button" class="lct-gate__go" data-lct-gate-go disabled>Continuer vers le paiement</button>
                </div>
            </div>`;
        document.body.appendChild(dialog);

        const accept = dialog.querySelector('[data-lct-gate-accept]');
        const go = dialog.querySelector('[data-lct-gate-go]');

        accept.addEventListener('change', () => {
            go.disabled = !accept.checked;
        });

        dialog.addEventListener('click', (e) => {
            if (e.target.closest('[data-lct-gate-close]')) close();
        });

        dialog.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                close();
            }
            if (e.key === 'Tab') {
                const f = [...dialog.querySelectorAll('input, button:not([disabled])')];
                if (!f.length) return;
                if (e.shiftKey && document.activeElement === f[0]) {
                    e.preventDefault();
                    f[f.length - 1].focus();
                } else if (!e.shiftKey && document.activeElement === f[f.length - 1]) {
                    e.preventDefault();
                    f[0].focus();
                }
            }
        });

        go.addEventListener('click', async () => {
            go.disabled = true;
            go.textContent = 'Redirection…';
            try {
                // Trace de l'accord sur la commande (visible dans l'admin Shopify)
                await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: JSON.stringify({ attributes: { 'Pré-commande acceptée': 'Oui' } }),
                });
            } catch (err) {
                /* on continue quand même vers le paiement */
            }
            window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        });
    };

    const open = (trigger) => {
        if (!dialog) build();
        lastTrigger = trigger;
        const accept = dialog.querySelector('[data-lct-gate-accept]');
        const go = dialog.querySelector('[data-lct-gate-go]');
        accept.checked = false;
        go.disabled = true;
        go.textContent = 'Continuer vers le paiement';
        dialog.hidden = false;
        document.documentElement.classList.add('lct-gate-lock');
        requestAnimationFrame(() => dialog.classList.add('is-open'));
        dialog.querySelector('.lct-gate__box').focus();
    };

    const close = () => {
        if (!dialog || dialog.hidden) return;
        dialog.classList.remove('is-open');
        document.documentElement.classList.remove('lct-gate-lock');
        setTimeout(() => {
            dialog.hidden = true;
        }, 200);
        lastTrigger?.focus?.();
    };

    // Phase de capture : on intercepte avant le thème (cart-drawer.js, cart.js)
    document.addEventListener(
        'click',
        (e) => {
            const btn = e.target.closest(CHECKOUT);
            if (!btn || btn.disabled || !hasBackorders()) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            open(btn);
        },
        true
    );

    // Touche Entrée dans un champ du formulaire panier
    document.addEventListener(
        'submit',
        (e) => {
            const submitter = e.submitter;
            if (!submitter || !submitter.matches(CHECKOUT) || !hasBackorders()) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            open(submitter);
        },
        true
    );
})();

/* ------------------------------------------------------------------
   Étiquette « Pré-commande » : ouverture / fermeture en glissé
   (tiroir + page panier, fonctionne aussi après rechargement du panier)
------------------------------------------------------------------ */
(() => {
    if (window.__lctBackorderToggle) return;
    window.__lctBackorderToggle = true;

    document.addEventListener('click', (e) => {
        const summary = e.target.closest('.lct-backorder > summary');
        if (!summary) return;

        const details = summary.parentElement;
        const text = details.querySelector('.lct-backorder__text');
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!text || reduce || !text.animate) return; // comportement natif

        e.preventDefault();
        details.lctAnim?.cancel();

        const closing = details.open;
        if (!closing) details.open = true;

        const style = getComputedStyle(text);
        const height = text.offsetHeight;
        const from = { height: '0px', opacity: 0, marginTop: '0px', paddingTop: '0px', paddingBottom: '0px' };
        const to = {
            height: `${height}px`,
            opacity: 1,
            marginTop: style.marginTop,
            paddingTop: style.paddingTop,
            paddingBottom: style.paddingBottom,
        };

        text.style.overflow = 'hidden';
        text.style.boxSizing = 'border-box'; // la hauteur mesurée inclut le padding
        const anim = text.animate(closing ? [to, from] : [from, to], {
            duration: 260,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        });
        details.lctAnim = anim;

        anim.onfinish = () => {
            if (closing) details.open = false;
            text.style.overflow = '';
            text.style.boxSizing = '';
            details.lctAnim = null;
        };
        anim.oncancel = () => {
            text.style.overflow = '';
            text.style.boxSizing = '';
        };
    });
})();

/* ------------------------------------------------------------------
   Lien « Estimer les frais de livraison » (sous le bouton Commander du tiroir)
   → ouvre le panneau Livraison des outils du panier
------------------------------------------------------------------ */
(() => {
    if (window.__lctShipLink) return;
    window.__lctShipLink = true;

    document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-lct-ship-open]');
        if (!link) return;
        const tools = link.closest('cart-drawer')?.querySelector('lct-cart-tools') || document.querySelector('lct-cart-tools');
        if (!tools || typeof tools.open !== 'function') return;
        e.preventDefault();
        tools.open('shipping', link);
    });
})();

/* LCT — Page panier : ouverture / fermeture en glissé des accordéons
   (code promo, frais de livraison, note) */
(() => {
    if (window.__lctAccSlide) return;
    window.__lctAccSlide = true;

    document.addEventListener('click', (e) => {
        const summary = e.target.closest('.lct-acc > summary');
        if (!summary) return;

        const details = summary.parentElement;
        const body = details.querySelector('.lct-acc__body');
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!body || reduce || !body.animate) return; // comportement natif

        e.preventDefault();
        details.lctAnim?.cancel();

        const closing = details.open;
        if (!closing) details.open = true;

        const style = getComputedStyle(body);
        const height = body.offsetHeight;
        const from = { height: '0px', opacity: 0, paddingTop: '0px', paddingBottom: '0px' };
        const to = {
            height: `${height}px`,
            opacity: 1,
            paddingTop: style.paddingTop,
            paddingBottom: style.paddingBottom,
        };

        body.style.overflow = 'hidden';
        body.style.boxSizing = 'border-box'; // la hauteur mesurée inclut le padding
        const anim = body.animate(closing ? [to, from] : [from, to], {
            duration: 280,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        });
        details.lctAnim = anim;

        const clean = () => {
            body.style.overflow = '';
            body.style.boxSizing = '';
        };
        anim.onfinish = () => {
            if (closing) details.open = false;
            clean();
            details.lctAnim = null;
        };
        anim.oncancel = clean;
    });
})();